import { formatTime } from "../utils/time";
import { upgradesForPathway } from "./pathway";
import { EVOLUTION_STATE_ITEMS } from "./picker";
/** An evolution's coin or point cost, short: "500", "12K". */
export const formatEvoCost = (coins) => coins >= 1000 ? `${coins / 1000}K` : String(coins);
/** Coins win over points; neither reads "Free". */
export const priceRow = (cost) => {
    const coins = cost?.coins ?? 0;
    const points = cost?.points ?? 0;
    if (coins === 0 && points > 0) {
        return {
            kind: "price",
            label: "Price",
            value: String(points),
            currency: "points",
        };
    }
    return {
        kind: "price",
        label: "Price",
        value: coins > 0 ? formatEvoCost(coins) : "Free",
        currency: "coins",
    };
};
/** What each source is called. Both apps label their rows from this. */
export const SOURCE_LABELS = {
    sbc: "SBC",
    objective: "Objective",
    "season-pass": "Season Pass",
    store: "Store",
    "token-store": "Token Store",
};
/**
 * Each source's icon, relative to the assets CDN. The base differs per app
 * (build-time env on web, a constant in the app), the paths do not.
 */
export const SOURCE_ICON_PATHS = {
    sbc: "/misc/ea/sbc-purple.png",
    objective: "/misc/ea/obj.png",
    "season-pass": "/misc/ea/sp.png",
    store: "/misc/ea/points.png",
    "token-store": "/misc/token.png",
};
const stateLabel = (state) => EVOLUTION_STATE_ITEMS.find((item) => item.state === state)?.label ?? "";
/**
 * How many matches the evolution needs. Unknown reads as "?" rather than 0 —
 * the two mean very different things when planning a chain.
 */
export const matchesLabel = (matches) => matches == null ? "?" : String(matches);
/**
 * Where the evolution comes from. Returns nothing at all for an evolution with
 * no source that the player has already unlocked — there the question doesn't
 * arise; only an unachieved one is worth marking "Unknown".
 */
export const sourceRow = (source, state) => {
    if (!source) {
        return state === "unachieved"
            ? { kind: "source", label: "Unknown", value: "" }
            : undefined;
    }
    // Season Pass names the tier in the value, so the label stays the source.
    const value = source.type === "token-store"
        ? source.cost != null
            ? String(source.cost)
            : ""
        : source.type !== "season-pass" && source.subname
            ? `${source.name}: ${source.subname}`
            : source.name;
    return {
        kind: "source",
        label: SOURCE_LABELS[source.type] ?? source.name,
        value,
        sourceType: source.type,
    };
};
export const describeEvolution = ({ evolution, categoryName, pathwayPath, now = Date.now(), }) => {
    const info = [
        { kind: "state", label: "State", value: stateLabel(evolution.state) },
    ];
    if (categoryName) {
        info.push({ kind: "category", label: "Category", value: categoryName });
    }
    if (evolution.source?.type !== "token-store") {
        info.push(priceRow(evolution.cost));
    }
    if (evolution.unlockBy) {
        info.push({
            kind: "unlockBy",
            label: "Unlock by",
            value: formatTime(evolution.unlockBy * 1000 - now),
        });
    }
    if (evolution.completeBy) {
        info.push({
            kind: "completeBy",
            label: "Complete by",
            value: formatTime(evolution.completeBy * 1000 - now),
        });
    }
    info.push({
        kind: "matches",
        label: "Matches",
        value: matchesLabel(evolution.matches),
    });
    // Only a repeatable evolution says so; "Repeats 0" is noise.
    if (evolution.repeats) {
        info.push({
            kind: "repeats",
            label: "Repeats",
            value: String(evolution.repeats),
        });
    }
    const source = sourceRow(evolution.source, evolution.state);
    if (source)
        info.push(source);
    const upgrades = upgradesForPathway(evolution, pathwayPath).flat();
    return {
        requirements: evolution.requirements ?? [],
        info,
        playStyleUpgrades: upgrades
            .filter((u) => u.type === "playstyle")
            // Plus first: it is the stronger reward and reads as the headline.
            .sort((a, b) => (a.isPlus === b.isPlus ? 0 : a.isPlus ? -1 : 1)),
        otherUpgrades: upgrades.filter((u) => u.type !== "playstyle"),
    };
};
// The card leads with what rules a player in or out at a glance.
const CARD_REQUIREMENT_ORDER = [
    "overall",
    "positions",
    "rarity",
    "playStylesPlus",
    "playStyles",
    "notRarity",
];
/** Requirements in the order the overview card lists them; the rest keep theirs. */
export const sortRequirementsForCard = (requirements) => {
    const rank = (key) => {
        const index = CARD_REQUIREMENT_ORDER.indexOf(key);
        return index >= 0 ? index : CARD_REQUIREMENT_ORDER.length;
    };
    return [...requirements].sort((a, b) => rank(a.key) - rank(b.key));
};
//# sourceMappingURL=info.js.map